import '../../global/actions/ui/misc';

import type { ChangeEvent } from 'react';
import type { FC } from '../../lib/teact/teact';
import React, {
  memo, useCallback, useEffect, useMemo, useRef, useState,
} from '../../lib/teact/teact';
import { getActions, withGlobal } from '../../global';

import type { ApiCountryCode, ApiNotification } from '../../api/types';
import type { GlobalState } from '../../global/types';
import type { LangCode } from '../../types';

import {
  AUTH_CONNECTION_TIMEOUT,
  AUTH_NOTIFICATION_DURATION,
  AUTH_TIMEOUT_NOTIFICATION_CLASS,
  AUTH_TIMEOUT_NOTIFICATION_MESSAGE,
  CONNECTION_NOTIFICATION_CLASS,
  CONNECTION_NOTIFICATION_MESSAGE,
} from '../../config';
import { requestMeasure } from '../../lib/fasterdom/fasterdom';
import { getCurrentTabId } from '../../util/establishMultitabRole';
import { preloadImage } from '../../util/files';
import preloadFonts from '../../util/fonts';
import { pick } from '../../util/iteratees';
import { getAccountsInfo, getAccountSlotUrl } from '../../util/multiaccount';
import { isValidEmail } from '../../util/validators';
import { IS_ELECTRON, IS_SAFARI } from '../../util/windowEnvironment';

import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';

import { signInWithGoogle, getGoogleIdToken } from '../../config/firebase';
import { isWebAuthnSupported } from '../../util/webauthn';

import Button from '../ui/Button';
import Checkbox from '../ui/Checkbox';
import InputText from '../ui/InputText';
import Loading from '../ui/Loading';

import monkeyPath from '../../assets/monkey.svg';
import googleIconPath from '../../assets/google.svg';

type StateProps = Pick<GlobalState, (
  'connectionState' | 'authState' |
  'authPhoneNumber' | 'authIsLoading' |
  'authIsLoadingQrCode' | 'authError' |
  'authRememberMe' | 'authNearestCountry'
)> & {
  language?: LangCode;
  phoneCodeList: ApiCountryCode[];
  notifications: ApiNotification[];
};

// const MIN_NUMBER_LENGTH = 7;

let isPreloadInitiated = false;

const AuthPhoneNumber: FC<StateProps> = ({
  // connectionState,
  authState,
  // authPhoneNumber,
  authIsLoading,
  authIsLoadingQrCode,
  authError,
  authRememberMe,
  notifications,
  // authNearestCountry,
  // phoneCodeList,
  // language,
}) => {
  const {
    setAuthEmail,
    setAuthRememberMe,
    // loadNearestCountry,
    // loadCountryList,
    clearAuthError,
    goToAuthQrCode,
    showNotification,
    dismissNotification,
    setAuthWebToken,
    loginWithPasskey,
    // setSettingOption,
  } = getActions();

  const lang = useLang();
  // eslint-disable-next-line no-null/no-null
  const inputRef = useRef<HTMLInputElement>(null);
  const connectionTimeoutRef = useRef<number | undefined>();
  const authTimeoutRef = useRef<number | undefined>();
  // const suggestedLanguage = getSuggestedLanguage();

  // const isConnected = connectionState === 'connectionStateReady';
  // const continueText = useLangString(isConnected ? suggestedLanguage : undefined, 'ContinueOnThisLanguage', true);
  // const [country, setCountry] = useState<ApiCountryCode | undefined>();
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>();
  const [validationError, setValidationError] = useState<string | undefined>();
  const [isLoadingSso, setIsLoadingSso] = useState(false);
  // const [isTouched, setIsTouched] = useState(false);
  // const [lastSelection, setLastSelection] = useState<[number, number] | undefined>();
  // const [isLoading, markIsLoading, unmarkIsLoading] = useFlag();
  const canSubmit = isValidEmail(String(phoneNumber));

  // useEffect(() => {
  //   if (isConnected && !authNearestCountry) {
  //     loadNearestCountry();
  //   }
  // }, [isConnected, authNearestCountry]);
  //
  // useEffect(() => {
  //   if (isConnected) {
  //     loadCountryList({ langCode: language });
  //   }
  // }, [isConnected, language]);

  const isAuthReady = authState === 'authorizationStateWaitPhoneNumber';

  // Connection timeout logic - start timeout on mount
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      // Check the current authState at the time timeout fires
      // We intentionally use authState from closure to check its value after timeout
      // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
      if (authState !== 'authorizationStateWaitPhoneNumber') {
        showNotification({
          message: CONNECTION_NOTIFICATION_MESSAGE,
          action: {
            action: 'reloadPage',
            payload: undefined,
          },
          actionText: 'Reload',
          duration: AUTH_NOTIFICATION_DURATION,
          className: CONNECTION_NOTIFICATION_CLASS,
        });
      }
    }, AUTH_CONNECTION_TIMEOUT);

    connectionTimeoutRef.current = timeoutId;

    return () => {
      clearTimeout(timeoutId);
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks-static-deps/exhaustive-deps
  }, []); // Run only once on mount

  // Clear timeout and dismiss notification when connection is ready
  useEffect(() => {
    if (isAuthReady) {
      // Clear the timeout
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = undefined;
      }

      // Find and dismiss our connection notification by className
      const connectionNotification = notifications.find(
        (notification) => notification && notification.className === CONNECTION_NOTIFICATION_CLASS,
      );
      if (connectionNotification) {
        dismissNotification({ localId: connectionNotification.localId });
      }
    }
  }, [isAuthReady, dismissNotification]);

  // Authentication timeout logic - start when authIsLoading becomes true
  useEffect(() => {
    if (authIsLoading) {
      // Start timeout when loading begins
      const timeoutId = window.setTimeout(() => {
        showNotification({
          message: AUTH_TIMEOUT_NOTIFICATION_MESSAGE,
          action: {
            action: 'reloadPage',
            payload: undefined,
          },
          actionText: 'Reload',
          duration: AUTH_NOTIFICATION_DURATION,
          className: AUTH_TIMEOUT_NOTIFICATION_CLASS,
        });
      }, AUTH_CONNECTION_TIMEOUT);

      authTimeoutRef.current = timeoutId;
    } else {
      // Clear timeout when loading ends (success or error)
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
        authTimeoutRef.current = undefined;
      }

      // Dismiss notification if it was shown by className
      const authNotification = notifications.find(
        (notification) => notification && notification.className === AUTH_TIMEOUT_NOTIFICATION_CLASS,
      );
      if (authNotification) {
        dismissNotification({ localId: authNotification.localId });
      }
    }
  }, [authIsLoading, showNotification, dismissNotification]);

  const isJustPastedRef = useRef(false);
  const handlePaste = useCallback(() => {
    isJustPastedRef.current = true;
    requestMeasure(() => {
      isJustPastedRef.current = false;
    });
  }, []);

  const handlePhoneNumberChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (authError) {
      clearAuthError();
    }
    setValidationError(undefined);

    // This is for further screens. We delay it until user input to speed up the initial loading.
    if (!isPreloadInitiated) {
      isPreloadInitiated = true;
      preloadFonts();
      void preloadImage(monkeyPath);
    }

    setPhoneNumber(e.target.value.trim());
  }, [authError, clearAuthError]);

  const handleKeepSessionChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setAuthRememberMe(e.target.checked);
  }, [setAuthRememberMe]);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (authIsLoading) {
      return;
    }

    if (canSubmit) {
      setAuthEmail({ email: String(phoneNumber).trim() });
    } else {
      setValidationError(lang('Login.InvalidEmail'));
    }
  }

  const handleGoToAuthQrCode = useCallback(() => {
    goToAuthQrCode();
  }, [goToAuthQrCode]);

  const handleSsoLogin = useLastCallback(async () => {
    if (isLoadingSso) return;

    setIsLoadingSso(true);
    try {
      let idToken: string | null = null;
      let email: string = '';

      if (IS_ELECTRON && window.electron?.googleSignIn) {
        // Use Electron's native Google Sign-In
        const result = await window.electron.googleSignIn();

        if (!result.success) {
          if (result.error === 'cancelled') {
            // User closed window - don't show error
            return;
          }
          showNotification({
            message: lang('Login.GoogleSignInFailed', result.error),
          });
          return;
        }

        idToken = result.idToken || null;
        email = result.email || '';
      } else {
        // Use Firebase popup for web
        const credential = await signInWithGoogle();
        idToken = await getGoogleIdToken() ?? null;
        email = credential.user.email || '';

        // eslint-disable-next-line no-console
        console.log('Google Sign-In successful:', {
          user: email,
          displayName: credential.user.displayName,
          idToken: idToken ? `${idToken.substring(0, 50)}...` : null,
        });
      }

      if (!idToken) {
        showNotification({
          message: lang('Login.GoogleTokenFailed'),
        });
        return;
      }

      // Pass Google ID Token to Telegram API via auth.importWebTokenAuthorization
      setAuthWebToken({ webAuthToken: idToken, email });
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error('SSO Login error:', error);

      // Handle specific Firebase errors
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorCode = (error as { code?: string })?.code;

      if (errorCode === 'auth/popup-closed-by-user') {
        // User closed popup - don't show error
        return;
      }

      if (errorCode === 'auth/cancelled-popup-request') {
        // Multiple popups - don't show error
        return;
      }

      showNotification({
        message: lang('Login.GoogleSignInFailed', errorMessage),
      });
    } finally {
      setIsLoadingSso(false);
    }
  });

  const handlePasskeyLogin = useLastCallback(() => {
    loginWithPasskey({});
  });

  // Check if we should show back button (has other authorized accounts - for multi-account flow)
  const accountsInfo = useMemo(() => getAccountsInfo(), []);
  const firstAccountSlot = useMemo(() => Object.keys(accountsInfo)[0], [accountsInfo]);
  const shouldShowBackButton = Boolean(firstAccountSlot);

  const handleBack = useLastCallback(() => {
    // Return to the first authorized account
    if (firstAccountSlot) {
      window.location.href = getAccountSlotUrl(Number(firstAccountSlot));
    }
  });

  return (
    <div id="auth-phone-number-form" className="custom-scroll">
      {shouldShowBackButton && (
        <div
          className="auth-back-button div-button"
          onClick={handleBack}
          role="button"
          tabIndex={0}
          title={lang('Back')}
          aria-label={lang('Back')}
        >
          <i className="icon icon-arrow-left" />
        </div>
      )}
      <div className="auth-form">
        <div id="logo" />
        <h1>SpaceChat</h1>
        <p className="note">{lang('PleaseEnterEmail')}</p>
        <form className="form" action="" onSubmit={handleSubmit}>
          <InputText
            ref={inputRef}
            id="sign-in-phone-number"
            testId="auth-email-input"
            label="Email"
            value={phoneNumber}
            error={(authError && lang(authError)) || validationError}
            inputMode="email"
            onChange={handlePhoneNumberChange}
            onPaste={IS_SAFARI ? handlePaste : undefined}
          />
          <Checkbox
            id="sign-in-keep-session"
            label={lang('Login.KeepSignedIn')}
            checked={Boolean(authRememberMe)}
            onChange={handleKeepSessionChange}
          />
          {(
            isAuthReady ? (
              <Button
                type="submit"
                testId={authIsLoading ? 'auth-next-button-loading' : 'auth-next-button-await'}
                ripple
                isLoading={authIsLoading}
              >{lang('Login.Next')}
              </Button>
            ) : (
              <Loading />
            )
          )}
          {isAuthReady && (
            <Button isText ripple isLoading={isLoadingSso} onClick={handleSsoLogin}>
              <img src={googleIconPath} alt="" className="google-icon" />
              {lang('Login.SignInWithGoogle')}
            </Button>
          )}
          {isAuthReady && isWebAuthnSupported() && (
            <Button isText ripple isLoading={authIsLoading} onClick={handlePasskeyLogin}>
              <i className="icon icon-key" />
              {lang('Login.SignInWithPasskey')}
            </Button>
          )}
          {isAuthReady && (
            <Button isText ripple isLoading={authIsLoadingQrCode} onClick={handleGoToAuthQrCode}>
              {lang('Login.QR.Login')}
            </Button>
          )}
          {/* {suggestedLanguage && suggestedLanguage !== language && continueText && ( */}
          {/*   <Button isText isLoading={isLoading} onClick={handleLangChange}>{continueText}</Button> */}
          {/* )} */}
        </form>
      </div>
    </div>
  );
};

export default memo(withGlobal(
  (global): StateProps => {
    const {
      settings: { byKey: { language } },
      countryList: { phoneCodes: phoneCodeList },
    } = global;

    const currentTabId = getCurrentTabId();

    return {
      ...pick(global, [
        'connectionState',
        'authState',
        'authPhoneNumber',
        'authIsLoading',
        'authIsLoadingQrCode',
        'authError',
        'authRememberMe',
        'authNearestCountry',
      ]),
      language,
      phoneCodeList,
      notifications: global.byTabId[currentTabId]?.notifications || [],
    };
  },
)(AuthPhoneNumber));
